/**
 * Conversational Agent Creator
 * Main chat interface for creating agents with Claude Custodian
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, Bot, User, Sparkles, CheckCircle2, AlertCircle } from 'lucide-react';
import { custodianClient } from '@/lib/custodianClient';
import CreationWizard from './CreationWizard';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ConversationState {
  conversation_id: string | null;
  stage: 'discovering' | 'designing' | 'testing' | 'deployed' | 'complete';
  messages: Message[];
  partial_agent?: any;
  agent_definition?: any;
}

export default function ConversationalCreator() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [conversation, setConversation] = useState<ConversationState>({
    conversation_id: conversationId || null,
    stage: 'discovering',
    messages: []
  });

  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);

  // Load existing conversation if ID provided
  useEffect(() => {
    if (conversationId) {
      loadConversation(conversationId);
    }
  }, [conversationId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation.messages]);

  const loadConversation = async (id: string) => {
    try {
      setLoading(true);
      const result = await custodianClient.getConversation(id);
      
      if (result.success) {
        setConversation({
          conversation_id: result.conversation.id,
          stage: result.conversation.stage,
          messages: result.conversation.messages,
          partial_agent: result.conversation.partial_agent,
          agent_definition: result.conversation.agent_definition
        });

        // Show wizard if agent is complete
        if (result.conversation.stage === 'complete' && result.conversation.agent_definition) {
          setShowWizard(true);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const startConversation = async (message: string) => {
    try {
      setLoading(true);
      setError(null);

      const result = await custodianClient.startConversation(message);

      if (result.success) {
        setConversation({
          conversation_id: result.conversation_id,
          stage: result.stage,
          messages: [
            { role: 'user', content: message, timestamp: new Date() },
            { role: 'assistant', content: result.reply, timestamp: new Date() }
          ],
          partial_agent: result.partial_agent
        });

        // Update URL with conversation ID
        navigate(`/agents/create/custodian/${result.conversation_id}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start conversation');
    } finally {
      setLoading(false);
    }
  };

  const continueConversation = async (message: string) => {
    if (!conversation.conversation_id) return;

    try {
      setLoading(true);
      setError(null);

      // Optimistically add user message
      const newMessages = [
        ...conversation.messages,
        { role: 'user' as const, content: message, timestamp: new Date() }
      ];
      setConversation(prev => ({ ...prev, messages: newMessages }));

      const result = await custodianClient.sendMessage(conversation.conversation_id, message);

      if (result.success) {
        setConversation(prev => ({
          ...prev,
          stage: result.stage,
          messages: [
            ...newMessages,
            { role: 'assistant' as const, content: result.reply, timestamp: new Date() }
          ],
          partial_agent: result.partial_agent,
          agent_definition: result.agent_definition
        }));

        // Show wizard if agent is complete
        if (result.stage === 'complete' && result.agent_definition) {
          setShowWizard(true);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const message = inputMessage.trim();
    setInputMessage('');

    if (conversation.conversation_id) {
      await continueConversation(message);
    } else {
      await startConversation(message);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Render wizard if agent is complete
  if (showWizard && conversation.agent_definition) {
    return (
      <CreationWizard
        agentDefinition={conversation.agent_definition}
        conversationId={conversation.conversation_id!}
        onBack={() => setShowWizard(false)}
      />
    );
  }

  // Stage indicator
  const getStageInfo = () => {
    switch (conversation.stage) {
      case 'discovering':
        return { label: 'Discovery', color: 'bg-blue-100 text-blue-700', icon: Sparkles };
      case 'designing':
        return { label: 'Designing', color: 'bg-purple-100 text-purple-700', icon: Bot };
      case 'testing':
        return { label: 'Testing', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle };
      case 'complete':
        return { label: 'Complete', color: 'bg-green-100 text-green-700', icon: CheckCircle2 };
      default:
        return { label: 'Starting', color: 'bg-gray-100 text-gray-700', icon: Sparkles };
    }
  };

  const stageInfo = getStageInfo();
  const StageIcon = stageInfo.icon;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Agent Custodian</h1>
            <p className="text-sm text-gray-600 mt-1">
              Conversational AI-powered agent creation with Claude 4.5 Sonnet
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={`${stageInfo.color} flex items-center gap-1.5 px-3 py-1.5`}>
              <StageIcon className="w-4 h-4" />
              {stageInfo.label}
            </Badge>
            {conversation.conversation_id && (
              <Button
                variant="outline"
                onClick={() => navigate('/agents/hub')}
              >
                Exit
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Welcome message if no conversation */}
          {conversation.messages.length === 0 && (
            <Card className="border-2 border-dashed border-gray-300">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Bot className="w-16 h-16 mx-auto mb-4 text-indigo-500" />
                  <h3 className="text-lg font-semibold mb-2">
                    Welcome to the Agent Custodian
                  </h3>
                  <p className="text-gray-600 mb-4">
                    I'm here to help you create powerful Volcano agents through natural conversation.
                    Just describe what you need, and I'll guide you through the process.
                  </p>
                  <div className="text-left mt-6 space-y-2">
                    <p className="font-medium text-sm text-gray-700">Example prompts:</p>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p>• "Create an agent that analyzes sensor data and predicts equipment failures"</p>
                      <p>• "I need an internal agent for managing product data in MongoDB"</p>
                      <p>• "Build an app agent that generates weekly reports from TimescaleDB"</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Messages */}
          {conversation.messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-5 h-5 text-indigo-600" />
                </div>
              )}
              
              <div
                className={`max-w-2xl rounded-lg px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white border border-gray-200'
                }`}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
                <p className={`text-xs mt-2 ${message.role === 'user' ? 'text-indigo-200' : 'text-gray-400'}`}>
                  {new Date(message.timestamp).toLocaleTimeString()}
                </p>
              </div>

              {message.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-gray-600" />
                </div>
              )}
            </div>
          ))}

          {/* Loading indicator */}
          {loading && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <Bot className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                  <span className="text-gray-600">Agent Custodian is thinking...</span>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-3">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                conversation.messages.length === 0
                  ? "Describe the agent you want to create..."
                  : "Type your response..."
              }
              disabled={loading}
              className="flex-1"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || loading}
              className="px-6"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

